import React, { useState, useRef, useEffect } from 'react';
import { chunkedUpload, initChunkedUpload, uploadChunk, logEvent } from '../services/api';
import { saveRecording, saveUploadProgress, deleteUploadProgress, deleteRecording } from '../utils/idbStorage';
import SavedRecordings from './SavedRecordings';
import PendingUploads from './PendingUploads';
import LoadingSpinner from './LoadingSpinner';
import { useT } from '../utils/i18n';

// How often MediaRecorder hands us a slice of audio. Each slice is uploaded as
// soon as it is produced, so a crash mid-recording costs at most this much.
const SLICE_MS = 10000;

const RecordingControls = ({ videoId, narrator, hasExistingAD, onUploadSuccess, recordLocked = false, recordLockReason = null, onRecordingStateChange = null }) => {
  const t = useT();
  const [isRecording, setIsRecording] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [slicesUploaded, setSlicesUploaded] = useState({ done: 0, total: 0 });
  const [error, setError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  // The live media stream, held in a ref so the unmount cleanup and handleRecorderStop
  // release the microphone reliably. (Reading it from state there captured a
  // stale `null` closure, so the mic was never turned off.)
  const streamRef = useRef(null);
  // Live upload session: slices are pushed to the server while the narrator records.
  const sessionRef = useRef(null);      // { uploadId, filename }
  const queueRef = useRef([]);          // slices waiting to be sent
  const drainRef = useRef(null);        // in-flight drain, so stop() can wait for it
  const uploadedRef = useRef(new Set()); // slice indices the server has confirmed

  const releaseStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
      console.log("Media stream stopped.");
    }
  };

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
        console.log("MediaRecorder stopped on cleanup.");
      }
      releaseStream();
    };
  }, []); // ✅ Only run on unmount

  // Let the page lock reenact while this narrator is recording (and vice versa).
  useEffect(() => {
    if (onRecordingStateChange) onRecordingStateChange(narrator, isRecording);
  }, [isRecording, narrator, onRecordingStateChange]);

  const handleLog = async (action, value = null) => {
    try {
      await logEvent({ video_id: videoId, action, value, narrator });
    } catch (err) {
      console.error("Failed to log event:", action, err);
    }
  };

  /** Send queued slices one at a time. Failures are left for the reconcile on stop. */
  const drainQueue = async () => {
    const uploadId = sessionRef.current?.uploadId;
    if (!uploadId || drainRef.current) return;

    drainRef.current = (async () => {
      while (queueRef.current.length > 0) {
        const slice = queueRef.current.shift();
        try {
          await uploadChunk(uploadId, slice.index, slice.blob);
          uploadedRef.current.add(slice.index);
          setSlicesUploaded({ done: uploadedRef.current.size, total: audioChunksRef.current.length });
        } catch (err) {
          // Recording carries on regardless: the slice is still in memory and
          // gets re-sent when the narrator stops.
          console.warn(`Slice ${slice.index} did not upload while recording:`, err.message);
        }
      }
    })();

    try {
      await drainRef.current;
    } finally {
      drainRef.current = null;
    }
  };

  const startRecording = async () => {
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setError(t('getUserMedia not supported on your browser!'));
      return;
    }

    let audioStream = null;

    try {
      audioStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          noiseSuppression: false,
          echoCancellation: false,
          autoGainControl: false
        }
      });
      streamRef.current = audioStream;
      setIsRecording(true);
      audioChunksRef.current = [];
      queueRef.current = [];
      uploadedRef.current = new Set();
      sessionRef.current = null;
      setIsStreaming(false);
      setSlicesUploaded({ done: 0, total: 0 });

      const options = {};
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        options.mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
        options.mimeType = 'audio/ogg;codecs=opus';
      }
      console.log("Using mimeType:", options.mimeType || "browser default");

      mediaRecorderRef.current = new MediaRecorder(audioStream, options);

      const mimeType = mediaRecorderRef.current.mimeType || 'audio/webm';
      const fileExtension = mimeType.split('/')[1].split(';')[0];
      const filename = `recording_${videoId}_${Date.now()}.${fileExtension}`;
      sessionRef.current = { uploadId: null, filename };

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size === 0) return;

        const index = audioChunksRef.current.length;
        audioChunksRef.current.push(event.data);
        setSlicesUploaded({ done: uploadedRef.current.size, total: audioChunksRef.current.length });

        // Queued even before the session exists; drainQueue picks them up as
        // soon as it does.
        queueRef.current.push({ index, blob: event.data });
        drainQueue();
      };

      mediaRecorderRef.current.onstop = handleRecorderStop;

      mediaRecorderRef.current.onerror = (event) => {
        console.error("MediaRecorder error:", event.error);
        setError(`${t('Recording error')}: ${event.error.name || t('Unknown error')}`);
        stopRecordingCleanup();
      };

      // Start capturing before touching the network: a slow or dead connection
      // must never delay the recording the narrator just asked for.
      mediaRecorderRef.current.start(SLICE_MS);
      handleLog('start_record');
      console.log("Recording started...");

      // Open the upload session alongside the recording. Its chunk count is not
      // known until the narrator stops, hence -1.
      initChunkedUpload(videoId, narrator, filename, -1)
        .then((session) => {
          sessionRef.current = { uploadId: session.upload_id, filename };
          setIsStreaming(true);
          console.log(`Streaming recording into upload session ${session.upload_id}`);
          drainQueue();
        })
        .catch((err) => {
          // Everything is still held in memory and uploaded when the narrator stops.
          console.warn("Could not open an upload session; will upload after recording:", err.message);
        });
    } catch (err) {
      console.error("Error starting recording:", err);
      setError(`${t('Could not start recording')}: ${err.name || err.message}`);
      setIsRecording(false);
      if (audioStream) {
        audioStream.getTracks().forEach(track => {
          if (track.readyState === 'live') track.stop();
        });
        streamRef.current = null;
      }
    }
  };

  const stopRecordingCleanup = () => {
    setIsRecording(false);
    releaseStream();
    console.log("Stop recording cleanup performed.");
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      console.log("Stopping recording...");
      // stop_record is logged by handleRecorderStop, which onstop reaches from
      // here *and* from every other way a recording can end.
      mediaRecorderRef.current.stop();
    } else {
      console.warn("Stop recording called but recorder not active.");
      stopRecordingCleanup();
    }
  };

  /**
   * The MediaRecorder's `onstop` — the microphone recording ending, not the
   * video player pausing. Every path that ends a take arrives here: the Stop
   * button, the unmount cleanup (an accidental back-navigation mid-recording),
   * and onerror → stopRecordingCleanup → releaseStream (ending the tracks ends
   * the recorder). So this is where `stop_record` is logged: logging it in
   * stopRecording() instead meant a take ended any other way was published with
   * no boundary at all, and the server then closed its window at "now" —
   * sweeping post-recording player events into the take.
   */
  const handleRecorderStop = async () => {
    console.log("MediaRecorder stopped. Finishing upload...");
    // Before the drain below, so the boundary sits tight against the audio
    // rather than after however long the last slices take to reach the server.
    handleLog('stop_record');
    setIsRecording(false);

    if (audioChunksRef.current.length === 0) {
      console.warn("No audio chunks recorded.");
      setError(t('No audio data was recorded. Please try again.'));
      stopRecordingCleanup();
      return;
    }

    // Let any slice already in flight finish before we reconcile with the server.
    if (drainRef.current) {
      await drainRef.current.catch(() => {});
    }

    const slices = audioChunksRef.current;
    const sliceSizes = slices.map(slice => slice.size);
    const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm';
    const audioBlob = new Blob(slices, { type: mimeType });
    const session = sessionRef.current || {};
    const filename = session.filename || `recording_${videoId}_${Date.now()}.webm`;
    const recordingId = `${videoId}_${Date.now()}_${narrator}`;

    audioChunksRef.current = [];
    queueRef.current = [];

    // Cache the recording before touching the network: however the upload goes,
    // the audio can be retried from this device.
    try {
      await saveRecording({
        id: recordingId,
        videoId: parseInt(videoId, 10),
        narrator,
        blob: audioBlob,
        filename,
        // Kept so a retry can re-cut the exact slices already on the server and
        // send only what is missing.
        sliceSizes,
        uploadId: session.uploadId || null,
        timestamp: new Date().toISOString()
      });
      console.log("Recording saved to IndexedDB cache:", recordingId);
    } catch (err) {
      console.error("Failed to save recording to cache:", err);
      // Continue with upload attempt even if caching fails
    }

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const response = await chunkedUpload(
        videoId,
        narrator,
        audioBlob,
        filename,
        {
          resumeUploadId: session.uploadId || undefined,
          sliceSizes,
          onProgress: setUploadProgress,
          onChunkComplete: async ({ uploadId, completedChunks, totalChunks }) => {
            try {
              await saveUploadProgress({
                recordingId,
                uploadId,
                videoId: parseInt(videoId, 10),
                narrator,
                completedChunks,
                totalChunks,
                timestamp: new Date().toISOString()
              });
            } catch (err) {
              console.warn("Failed to save upload progress:", err);
            }
          }
        }
      );

      console.log("Upload successful:", response);
      setIsUploading(false);
      setUploadProgress(0);
      onUploadSuccess(response);

      try {
        await deleteRecording(recordingId);
        await deleteUploadProgress(recordingId);
        console.log("Cached recording and progress removed after successful upload:", recordingId);
      } catch (cacheErr) {
        console.warn("Could not remove cached recording:", cacheErr);
      }
    } catch (err) {
      console.error("Upload failed:", err.response?.data || err.message);
      setError(`อัพโหลดไม่สำเร็จ แต่บันทึกเสียงไว้แล้ว สามารถลองอัพโหลดใหม่ได้: ${err.message || t('Unknown error')}`);
      setIsUploading(false);
      setUploadProgress(0);
      handleLog('upload_fail', err.response?.data?.detail || err.message);
    } finally {
      sessionRef.current = null;
      setIsStreaming(false);
      // Surface whatever the server is still holding for this recording.
      setRefreshToken(token => token + 1);
      releaseStream();
    }
  };

  const handleRecovered = (response) => {
    setRefreshToken(token => token + 1);
    onUploadSuccess(response);
  };

  let buttonText = '';
  if (narrator === "Waan") {
    buttonText = hasExistingAD ? 'เริ่มอัดเพิ่ม (หวาน)' : 'เริ่มอัด (หวาน)';
  } else {
    buttonText = hasExistingAD ? 'เริ่มอัดเพิ่ม (ว่าน)' : 'เริ่มอัด (ว่าน)';
  }

  return (
    <div className="recording-controls card p-3">
      {!isRecording && (
        <>
          <button
            className={`btn ${hasExistingAD ? 'btn-info' : 'btn-primary'}`}
            onClick={startRecording}
            disabled={isUploading || recordLocked}
          >
            {isUploading ? 'กำลังอัพโหลดเสียง...' : buttonText}
          </button>
          {recordLocked && recordLockReason && (
            <small className="text-muted mt-2 d-block">{recordLockReason}</small>
          )}
        </>
      )}
      {isRecording && (
        <>
          <button className="btn btn-danger" onClick={stopRecording} disabled={isUploading}>
            หยุดอัด
          </button>
          <small className="text-muted mt-2 d-block">
            {isStreaming
              ? `กำลังอัพโหลดระหว่างอัด ${slicesUploaded.done}/${slicesUploaded.total} ส่วน`
              : 'ยังไม่ได้เชื่อมต่อเซิร์ฟเวอร์ จะอัพโหลดหลังหยุดอัด'}
          </small>
        </>
      )}
      {isUploading && (
        <div className="mt-2">
          <div className="d-flex align-items-center mb-2">
            <LoadingSpinner small />
            <span className="ms-2">
              {uploadProgress < 90
                ? `กำลังอัพโหลดเสียง... ${uploadProgress}%`
                : uploadProgress < 100
                  ? 'กำลังประมวลผลเสียง...'
                  : 'เสร็จสิ้น!'}
            </span>
          </div>
          <div className="progress" style={{ height: '20px' }}>
            <div
              className={`progress-bar ${uploadProgress >= 100 ? 'bg-success' : 'progress-bar-striped progress-bar-animated'}`}
              role="progressbar"
              style={{ width: `${uploadProgress}%` }}
              aria-valuenow={uploadProgress}
              aria-valuemin="0"
              aria-valuemax="100"
            >
              {uploadProgress}%
            </div>
          </div>
          <small className="text-muted mt-1 d-block">
            {uploadProgress >= 90
              ? 'เสียงอยู่บนเซิร์ฟเวอร์แล้ว หากหลุดสามารถกดประมวลผลใหม่ได้'
              : 'อัพโหลดทีละส่วน - หากเครือข่ายขาด สามารถลองอัพโหลดต่อได้'}
          </small>
        </div>
      )}
      {error && <div className="alert alert-danger mt-2">{error}</div>}

      {/* Recordings the server is holding: recoverable from any device. Hidden
          while recording, so the session being streamed into right now is never
          offered up for processing. */}
      {!isRecording && (
        <PendingUploads
          videoId={parseInt(videoId, 10)}
          narrator={narrator}
          refreshToken={refreshToken}
          onRecovered={handleRecovered}
        />
      )}

      {/* Recordings cached on this device that can be re-uploaded */}
      <SavedRecordings
        videoId={parseInt(videoId, 10)}
        narrator={narrator}
        refreshToken={refreshToken}
        onUploadSuccess={handleRecovered}
      />
    </div>
  );
};

export default RecordingControls;
